import { looksLikePool } from './chunk-LON6RMEV.js';
import { graphTokenRefs, tokenMetadataKey, buildTokenMetadataIndex } from './chunk-U6MRWH6C.js';
import { unwrapData } from './chunk-HIWBYL4O.js';
import { normalizeAddress } from './chunk-G7JKLWJD.js';
import { toCanonicalChainId } from './chunk-CMRKVSXA.js';
import { z } from 'zod';

// src/tokens/graphMetadata.ts
function graphTokenMetadata(graph, definitions) {
  const edges = graph?.edges;
  if (!Array.isArray(edges)) return definitions;
  const observed = /* @__PURE__ */ new Map();
  for (const edge of edges) {
    const [ref] = graphTokenRefs({ edges: [edge] });
    if (!ref) continue;
    const key = tokenMetadataKey(ref.chainId, ref.address);
    const entry = observed.get(key) ?? { row: { ...ref, isSpam: false }, conflicts: /* @__PURE__ */ new Set() };
    if (Array.isArray(edge.token_metadata_conflicts)) {
      for (const field of edge.token_metadata_conflicts) if (typeof field === "string") entry.conflicts.add(field);
    }
    for (const field of ["decimals", "symbol"]) {
      const value = edge[field];
      const valid = field === "decimals" ? typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 255 : typeof value === "string" && value.length > 0 && value.length <= 80;
      if (!valid) continue;
      if (entry.row[field] !== void 0 && entry.row[field] !== value) entry.conflicts.add(field);
      Object.assign(entry.row, { [field]: value });
    }
    observed.set(key, entry);
  }
  const rows = new Map(definitions.byKey);
  for (const [key, { row, conflicts }] of observed) {
    const merged = { ...rows.get(key), ...row, isSpam: rows.get(key)?.isSpam ?? false };
    for (const field of ["decimals", "symbol"]) if (conflicts.has(field)) delete merged[field];
    rows.set(key, merged);
  }
  return buildTokenMetadataIndex([...rows.values()]);
}

// src/tokens/holderView.ts
var HOLDER_VIEW_POLICY = Object.freeze({ graphNodes: 50, manualNodes: 20, largeSharePercent: 1, mediumSharePercent: 0.1 });
var HOLDER_BAND_LABELS = {
  large: `At least ${HOLDER_VIEW_POLICY.largeSharePercent}%`,
  medium: `${HOLDER_VIEW_POLICY.mediumSharePercent}% to under ${HOLDER_VIEW_POLICY.largeSharePercent}%`,
  small: `Under ${HOLDER_VIEW_POLICY.mediumSharePercent}%`,
  unknown: "Unknown supply share"
};
function holderBand(holder) {
  const share = holder.percentage_of_supply;
  if (share == null || !Number.isFinite(share) || share < 0 || share > 100) return "unknown";
  return share >= HOLDER_VIEW_POLICY.largeSharePercent ? "large" : share >= HOLDER_VIEW_POLICY.mediumSharePercent ? "medium" : "small";
}
function holderView(holders, chain, excluded = [], manual = [], band) {
  const key = (a) => normalizeAddress(a, chain);
  const hidden = new Set(excluded.map(key));
  const rows = /* @__PURE__ */ new Map();
  for (const holder of holders) rows.set(key(holder.address), holder);
  for (const address of manual.slice(0, HOLDER_VIEW_POLICY.manualNodes)) if (!rows.has(key(address))) rows.set(key(address), { address, label: "Manual tracking \xB7 balance unknown" });
  return [...rows].filter(([id, row]) => !hidden.has(id) && (!band || holderBand(row) === band)).map(([, row]) => row);
}

// src/tokens/index.ts
var holderSchema = z.object({
  address: z.string().min(1),
  balance: z.string().nullish(),
  balance_formatted: z.string().nullish(),
  usd_value: z.number().finite().nonnegative().nullish(),
  percentage_of_supply: z.number().finite().min(0).max(100).nullish(),
  is_contract: z.boolean().nullish(),
  label: z.string().nullish()
});
var holderObservationSchema = z.object({
  provider_id: z.string().nullish(),
  fetched_at: z.number().int().nonnegative().nullish(),
  snapshot_block_number: z.number().int().nonnegative().nullish(),
  snapshot_block_hash: z.string().nullish(),
  supply_basis: z.enum(["unknown", "total_supply", "circulating_supply"]),
  scope: z.enum(["page", "aggregate"]),
  coverage: z.enum(["unknown", "partial", "complete"]),
  index_status: z.string().nullish(),
  index_error: z.string().nullish(),
  verified_addresses: z.number().nullish(),
  verification_total: z.number().nullish(),
  indexed_through_block: z.number().int().nonnegative().nullish(),
  balance_verified: z.boolean().optional(),
  finality: z.string().nullish(),
  from_block: z.number().int().nonnegative().nullish(),
  state_adjustments: z.number().int().nonnegative().nullish(),
  omitted_holders: z.number().int().nonnegative(),
  availability: z.enum(["available", "unknown", "unavailable"])
});
var holderCohortsSchema = z.object({
  method: z.literal("distributed_holder_coordination"),
  method_version: z.number().int(),
  ownership_proven: z.literal(false),
  score_semantics: z.literal("uncalibrated_evidence_strength"),
  from_block: z.number().int(),
  through_block: z.number().int(),
  status: z.string().optional(),
  custody_coverage: z.string().optional(),
  omissions: z.record(z.string(), z.number()),
  groups: z.array(z.object({
    id: z.string(),
    members: z.array(z.string()),
    distributor: z.string(),
    claim: z.literal("candidate_common_control"),
    evidence_strength: z.number(),
    balance_raw: z.string().nullable(),
    percentage_of_supply: z.number().min(0).max(100).nullable(),
    supporting_transactions: z.array(z.string()),
    limitations: z.array(z.string()),
    edges: z.array(z.object({
      chain_id: z.string(),
      token_address: z.string(),
      source: z.string(),
      target: z.string(),
      raw_amount: z.string(),
      block_number: z.number(),
      block_hash: z.string(),
      tx_hash: z.string(),
      log_index: z.number()
    }))
  }))
});
var tokenOwnersPageSchema = z.object({
  chain_id: z.string(),
  identifier: z.string(),
  holders: z.array(holderSchema),
  cursor: z.string().nullish(),
  live: z.boolean(),
  observation: holderObservationSchema.optional(),
  holder_cohorts: holderCohortsSchema.nullish()
});
async function fetchTokenOwners(client, chain, identifier, options) {
  const page = tokenOwnersPageSchema.parse(unwrapData(await client.get(tokenReadPath(chain, identifier, "owners"), {
    query: { ...holderBlockQuery(options.blockNumber), limit: options.limit, ...options.cursor ? { cursor: options.cursor } : {} },
    signal: options.signal,
    cacheTtlMs: 0
  })));
  validateHolderBlock(page.observation, options.blockNumber, page.holders.length > 0);
  return page;
}
var holderStatsSchema = z.object({
  token_address: z.string(),
  chain: z.string().nullish(),
  total_holders: z.number().int().nonnegative().nullish(),
  /** Top-N holders' share of supply, ascending by rank. `supply` stays a string: it is past float precision. */
  supply_bands: z.array(z.object({
    rank: z.number().int().positive(),
    supply: z.string().nullish(),
    // No max: a provider rounding the top-100 share to 100.01 must not fail the whole document.
    supply_percent: z.number().finite().nonnegative().nullish()
  })),
  /** Net holder change over trailing windows, shortest first; a window the service could not read has no seconds. */
  holder_changes: z.array(z.object({
    window: z.string(),
    window_seconds: z.number().int().positive().nullish(),
    change: z.number().int().nullish(),
    change_percent: z.number().finite().nullish()
  })),
  holders_by_acquisition: z.record(z.string(), z.number().int().nonnegative()),
  holder_distribution: z.record(z.string(), z.number().int().nonnegative())
});
var tokenHolderStatsSchema = z.object({
  chain_id: z.string(),
  identifier: z.string(),
  stats: holderStatsSchema.nullable(),
  source: z.string().nullish(),
  live: z.boolean(),
  observation: holderObservationSchema.optional()
});
async function fetchTokenHolderStats(client, chain, identifier, options = {}) {
  const read = tokenHolderStatsSchema.parse(unwrapData(await client.get(tokenReadPath(chain, identifier, "holder-stats"), {
    query: holderBlockQuery(options.blockNumber),
    signal: options.signal,
    cacheTtlMs: 0
  })));
  validateHolderBlock(read.observation, options.blockNumber, read.stats != null);
  return read;
}
function validateHolderBlock(observation, requested, hasData) {
  if (requested !== void 0 && hasData && observation?.snapshot_block_number !== requested) {
    throw new Error("The holder provider did not attest the requested snapshot block.");
  }
}
function tokenReadPath(chain, identifier, segment) {
  const canonical = toCanonicalChainId(chain) ?? chain;
  return `/tokens/${encodeURIComponent(canonical)}/${encodeURIComponent(normalizeAddress(identifier, canonical))}/${segment}`;
}
function tokenHolderSubject(hints) {
  const roles = (hints.roles ?? []).map((role) => String(role ?? "").toLowerCase().replace(/_/g, "-"));
  if (looksLikePool(hints)) {
    return roles.some((role) => ["uniswap-v2-pool", "uniswap-v2-pair"].includes(role)) ? "lp-token" : "pool-positions";
  }
  if (hints.addressType === "token" || roles.some((role) => ["erc20", "erc-20", "spl-token", "fungible-token"].includes(role))) return "token";
  return null;
}
function uniqueTokenHolders(pages, chain) {
  const holders = /* @__PURE__ */ new Map();
  for (const page of consistentTokenHolderPages(pages)) if (page.live) for (const holder of page.holders) {
    const key = normalizeAddress(holder.address, chain);
    if (!holders.has(key)) holders.set(key, holder);
  }
  return [...holders.values()];
}
function consistentTokenHolderPages(pages) {
  const first = pages[0];
  if (!first) return [];
  const identity = (p) => JSON.stringify([
    toCanonicalChainId(p.chain_id) ?? p.chain_id,
    normalizeAddress(p.identifier, p.chain_id),
    p.observation?.provider_id ?? null,
    p.observation?.snapshot_block_number ?? null,
    p.observation?.snapshot_block_hash ?? null,
    p.observation?.supply_basis ?? "unknown"
  ]);
  const key = identity(first);
  const changed = pages.findIndex((page) => identity(page) !== key);
  return pages.slice(0, changed < 0 ? pages.length : changed);
}
function nextTokenOwnersCursor(page, used, loadedRows, ceiling) {
  return page.live && page.cursor && !used.includes(page.cursor) && loadedRows < ceiling ? page.cursor : void 0;
}
function holderBlockQuery(blockNumber) {
  if (blockNumber === void 0) return {};
  if (!Number.isSafeInteger(blockNumber) || blockNumber < 0) throw new Error("Snapshot block must be a non-negative integer");
  return { block_number: blockNumber };
}

export { HOLDER_BAND_LABELS, HOLDER_VIEW_POLICY, consistentTokenHolderPages, fetchTokenHolderStats, fetchTokenOwners, graphTokenMetadata, holderBand, holderBlockQuery, holderCohortsSchema, holderView, nextTokenOwnersCursor, tokenHolderStatsSchema, tokenHolderSubject, tokenOwnersPageSchema, uniqueTokenHolders };
//# sourceMappingURL=chunk-T63J4YOQ.js.map
//# sourceMappingURL=chunk-T63J4YOQ.js.map